import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api'; // Assuming BASE_URL is correctly defined in your config/api.js
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf'; // Import jsPDF
import 'jspdf-autotable'; // Import for table support
import * as XLSX from 'xlsx'; // Import xlsx for Excel export

const SubCategoryList = () => {
    // State for subcategories and filtered subcategories
    const [subCategories, setSubCategories] = useState([]);
    const [filteredSubCategories, setFilteredSubCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    // Fetch subcategories when the component mounts
    useEffect(() => {
        fetch(`${BASE_URL}subcategories`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setSubCategories(data.data); // Store the subcategories data
                    setFilteredSubCategories(data.data); // Set initial filtered subcategories
                } else {
                    alert('Error fetching subcategories');
                }
            })
            .catch(error => console.error('Error fetching subcategories:', error));
    }, []);

    // Handle search term change
    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);

        // Filter subcategories based on the search term
        const filtered = subCategories.filter(subcategory =>
            subcategory.name.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredSubCategories(filtered);
    };

    // Navigate to the edit subcategory page
    const handleEdit = (id) => {
        navigate(`/EditSubCategory/${id}`);
    };

    // Navigate to the add subcategory page
    const handleAdd = () => {
        navigate('/AddSubCategory');
    };

    // Handle subcategory deletion
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this subcategory?")) {
            fetch(`${BASE_URL}subcategories/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(response => response.json())
                .then(data => {
                    if (data.status === 1) {
                        // Remove the deleted subcategory from both lists (subCategories and filteredSubCategories)
                        setSubCategories(subCategories.filter(subcategory => subcategory.id !== id));
                        setFilteredSubCategories(filteredSubCategories.filter(subcategory => subcategory.id !== id));
                    }
                })
                .catch(error => console.error('Error deleting subcategory:', error));
        }
    };

    // Export filtered subcategories to PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Add Title
        doc.text('Subcategory List', 14, 10); // Title at top of PDF

        // Prepare table data for PDF (only ID, Name, and Image)
        const tableData = filteredSubCategories.map(subcategory => [
            subcategory.id,
            subcategory.name || 'N/A',
            subcategory.image ? `${BASE_URL}${subcategory.image}` : 'No Image',
        ]);

        // Add table to PDF with autoTable
        doc.autoTable({
            head: [['ID', 'Name', 'Image']], // Table headers for ID, Name, and Image only
            body: tableData,
        });

        // Save the PDF with a filename
        doc.save('subcategory_list.pdf');
    };

    // Export filtered subcategories to Excel
    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredSubCategories.map(subcategory => ({
            ID: subcategory.id,
            Name: subcategory.name || 'N/A',
            Image: subcategory.image ? `${BASE_URL}${subcategory.image}` : 'No Image',
            CreatedAt: new Date(subcategory.created_at).toLocaleString(),
            UpdatedAt: new Date(subcategory.updated_at).toLocaleString(),
        })));
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Subcategory List");

        // Save the Excel file with a filename
        XLSX.writeFile(workbook, 'subcategory_list.xlsx');
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Subcategory List</h3>
                        </div>
                        <div className="col-12 col-md-6 order-md-2 order-first">
                            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
                                <ol className="breadcrumb">
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Subcategory Table */}
            <section className="section">
                <div className="row" id="basic-table">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-md-6">
                                    </div>
                                    <div className="col-md-6 text-end">
                                        <button className="btn btn-primary" onClick={handleAdd}>Add Subcategory</button>
                                        {/* Export to PDF Button */}
                                        <button className="btn btn-secondary ms-2" onClick={handleExportPDF}>Export to PDF</button>
                                        {/* Export to Excel Button */}
                                        {/* <button className="btn btn-secondary ms-2" onClick={handleExportExcel}>Export to Excel</button> */}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label htmlFor="searchTerm" className="form-label">Search by Name</label>
                                    <input
                                        type="text"
                                        id="searchTerm"
                                        className="form-control"
                                        placeholder="Search subcategory name"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Image</th>
                                                <th className="text-end">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredSubCategories.map(subcategory => (
                                                <tr key={subcategory.id}>
                                                    <td>{subcategory.id}</td>
                                                    <td>{subcategory.name || 'N/A'}</td>
                                                    <td>
                                                        <img
                                                            src={`${BASE_URL}${subcategory.image}`}
                                                            alt={subcategory.name}
                                                            width="100"
                                                            height="100"
                                                        />
                                                    </td>
                                                    <td className="text-end">
                                                        <button className="btn btn-success" onClick={() => handleEdit(subcategory.id)}>Edit</button>
                                                        <button className="btn btn-danger ms-2" onClick={() => handleDelete(subcategory.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SubCategoryList;
